// Root layout: provides the server-owned user identity to every screen, guards
// the role-gated sections, and renders the "⋯ Screens" dev launcher (a testing
// aid — it would not ship). The old Owner/Installer toggle is GONE: role comes
// from the account (APP-ACC-09), never from a client switch (SYS-SW-07) — an
// installer reaches their workspace via the Me-menu entry that appears once the
// back-office has approved them.
import { useEffect, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useMatches, Link } from 'react-router-dom'
import { UserProvider, useUser } from './UserContext'
import { ROUTES, type RouteHandle } from './nav'
import { api } from '../api'
import { applyTheme } from './theme'
import { canUseWorkspace, defaultRouteFor } from './workspace'

function ThemeSync() {
  const { user } = useUser()
  useEffect(() => {
    if (!user) return
    let live = true
    void api.getUiPrefs().then((prefs) => {
      if (live) applyTheme(prefs.theme === 'light' ? 'light' : 'dark')
    }).catch(() => {})
    return () => { live = false }
  }, [user?.id])
  return null
}

function DevLauncher() {
  const { pathname } = useLocation()
  // Never ship prototype chrome accidentally. The direct /screens route stays
  // available for explicit review; the floating shortcut is dev/opt-in only.
  if ((!import.meta.env.DEV && import.meta.env.VITE_SHOW_SCREEN_INDEX !== 'true') || pathname === ROUTES.login) return null
  return (
    <div className="devswitch" role="group" aria-label="prototype controls">
      <Link to={ROUTES.screens} style={{ padding: '6px 12px', color: 'var(--muted)', fontWeight: 600 }}>
        ⋯ Screens
      </Link>
    </div>
  )
}

/** Client-side courtesy guard for the role-gated sections — the API's 403s are
 *  the real gate. The required role comes from the MATCHED ROUTE's `handle`
 *  (router.tsx declares each gated group with `handle: { role }`), so a new gated
 *  screen added inside its group is gated by construction — there is no separate
 *  prefix list to forget. On a gated route we HOLD (render nothing) until the
 *  first identity fetch resolves, so a non-privileged user never briefly renders
 *  a gated screen (and fires its API calls) before the redirect. Non-gated
 *  routes render at once. */
function RoleGuard({ children }: { children: ReactNode }) {
  const { user, loading } = useUser()
  const handle = useMatches()
    .map((m) => m.handle as RouteHandle | undefined)
    .find((candidate) => candidate?.role || candidate?.roles || candidate?.workspace)
  if (handle) {
    if (loading) return null // wait for identity before rendering a gated screen
    if (!user) return <Navigate to={ROUTES.login} replace />
    if (handle.role && user.role !== handle.role) return <Navigate to={defaultRouteFor(user)} replace />
    if (handle.roles && !handle.roles.includes(user.role)) return <Navigate to={defaultRouteFor(user)} replace />
    if (handle.workspace && !canUseWorkspace(user, handle.workspace)) return <Navigate to={defaultRouteFor(user)} replace />
  }
  return <>{children}</>
}

export default function Layout() {
  return (
    <UserProvider>
      <ThemeSync />
      <RoleGuard>
        <Outlet />
      </RoleGuard>
      <DevLauncher />
    </UserProvider>
  )
}
